import { motion } from "framer-motion";

/**
 * Narrava logo mark.
 * - Inner radial glow behind the "N"
 * - Breathing pulse (4s cycle)
 * - Auto gradient shift (peach → amber → rose-gold) on mount + hover (desktop)
 * - Faint script flourish beneath representing "Vā" (flow)
 */
export default function Logo({ size = 96, animated = true, onClick }) {
  const flourishH = Math.round(size * 0.22);
  return (
    <div
      data-testid="narrava-logo"
      onClick={onClick}
      style={{ width: size, height: size + flourishH, position: "relative", userSelect: "none" }}
    >
      <motion.div
        initial={{ scale: 1 }}
        animate={
          animated
            ? { scale: [1, 1.03, 1] }
            : { scale: 1 }
        }
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        whileHover={{ scale: 1.05 }}
        className="logo-tile"
        style={{
          width: size,
          height: size,
          borderRadius: size * 0.28,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: onClick ? "pointer" : "default",
          boxShadow:
            "0 12px 44px -10px rgba(212, 175, 122, 0.55), inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -14px 30px -10px rgba(120, 70, 30, 0.55)",
          overflow: "hidden",
        }}
      >
        {/* Gradient base */}
        <div
          className="logo-gradient"
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            background:
              "linear-gradient(135deg, #f3d8a7 0%, #eac88a 35%, #d4af7a 65%, #a07544 100%)",
          }}
        />
        {/* Inner radial glow */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            background:
              "radial-gradient(circle at 50% 42%, rgba(255, 246, 216, 0.75) 0%, rgba(255, 234, 180, 0.3) 28%, transparent 62%)",
            mixBlendMode: "screen",
          }}
        />
        {/* Highlight arc */}
        <div
          style={{
            position: "absolute",
            inset: 1,
            borderRadius: "inherit",
            background:
              "radial-gradient(ellipse 85% 50% at 50% 0%, rgba(255,255,255,0.55) 0%, transparent 55%)",
            pointerEvents: "none",
          }}
        />
        {/* The letter */}
        <span
          className="font-sacred"
          style={{
            position: "relative",
            fontStyle: "italic",
            fontWeight: 500,
            fontSize: size * 0.5,
            color: "#1a0d1d",
            textShadow: "0 1px 0 rgba(255, 240, 200, 0.45)",
            lineHeight: 1,
            letterSpacing: "-0.02em",
          }}
        >
          N
        </span>
      </motion.div>

      {/* Script flourish — faint curved line, "Vā" flow */}
      <svg
        width={size * 1.05}
        height={flourishH}
        viewBox="0 0 110 22"
        fill="none"
        style={{
          position: "absolute",
          left: "50%",
          bottom: 0,
          transform: "translateX(-50%)",
          opacity: 0.55,
          pointerEvents: "none",
        }}
        aria-hidden
      >
        <defs>
          <linearGradient id="flourish-grad" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#d4af7a" stopOpacity="0" />
            <stop offset="50%" stopColor="#eac88a" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#d4af7a" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M 6 12 Q 25 4, 55 12 T 104 12"
          stroke="url(#flourish-grad)"
          strokeWidth="1.1"
          strokeLinecap="round"
          fill="none"
        />
        <circle cx="55" cy="12" r="1.1" fill="#eac88a" opacity="0.85" />
      </svg>
    </div>
  );
}
